import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { ApiFout, api } from '@/lib/api';
import type { SoepFeedbackAntwoord, SoepFeedbackRegel } from '@/lib/api-types';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, MessageSquare, RefreshCw, Search, ThumbsDown, ThumbsUp } from 'lucide-react';

/**
 * De beoordelingen van gegenereerde SOEP-verslagen.
 *
 * Dit tabblad beantwoordt de vraag die ai_usage_logs en het foutenlog niet kunnen
 * beantwoorden: deugde het verslag? Een generatie die netjes lukt en inhoudelijk
 * onbruikbaar is, ziet er in die twee tabellen precies zo uit als een geslaagde.
 *
 * Het filteren gebeurt op de server, zoals bij het foutenlog. De tellers boven de
 * tabel horen bij dezelfde selectie maar negeren het oordeelfilter: de vraag is
 * hoeveel duimen omhoog er tegenover hoeveel omlaag staan, en die is niet te
 * beantwoorden met een teller die meebeweegt met de duim waarop je filtert.
 */

const PAGINA = 100;

/** Leesbare namen voor de redenen uit routes/feedback.ts. */
const REDEN_LABELS: Record<string, string> = {
  onjuist: 'Onjuiste inhoud',
  onvolledig: 'Mist informatie',
  indeling: 'Verkeerde SOEP-indeling',
  te_lang: 'Te uitgebreid',
  te_kort: 'Te beknopt',
  taal: 'Taal of stijl',
  anders: 'Anders',
};

function formatTijd(waarde: string): string {
  const dt = new Date(waarde);
  return `${dt.toLocaleDateString('nl-NL')} ${dt.toLocaleTimeString('nl-NL', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

export function FeedbackTab() {
  const [entries, setEntries] = useState<SoepFeedbackRegel[]>([]);
  const [summary, setSummary] = useState<{ up: number; down: number }>({ up: 0, down: 0 });
  const [tags, setTags] = useState<Array<{ tag: string; count: number }>>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const [zoek, setZoek] = useState('');
  /** Wat er daadwerkelijk naar de server gaat; loopt achter op `zoek`. */
  const [zoekTerm, setZoekTerm] = useState('');
  const [oordeel, setOordeel] = useState('all');
  const [reden, setReden] = useState('all');
  const [van, setVan] = useState('');
  const [tot, setTot] = useState('');

  useEffect(() => {
    const t = setTimeout(() => {
      setZoekTerm(zoek.trim());
      setOffset(0);
    }, 350);
    return () => clearTimeout(t);
  }, [zoek]);

  const laden = useCallback(async () => {
    setIsLoading(true);
    try {
      const antwoord = await api.get<SoepFeedbackAntwoord>('/admin/feedback', {
        query: {
          limit: PAGINA,
          offset,
          ...(zoekTerm ? { q: zoekTerm } : {}),
          ...(oordeel !== 'all' ? { rating: oordeel } : {}),
          ...(reden !== 'all' ? { tag: reden } : {}),
          ...(van ? { from: van } : {}),
          ...(tot ? { to: tot } : {}),
        },
      });
      setEntries(antwoord.entries);
      setSummary(antwoord.summary);
      setTags(antwoord.tags);
      setTotal(antwoord.total);
    } catch (fout) {
      toast.error(fout instanceof ApiFout ? fout.message : 'Kon de feedback niet laden');
    } finally {
      setIsLoading(false);
    }
  }, [offset, zoekTerm, oordeel, reden, van, tot]);

  useEffect(() => {
    void laden();
  }, [laden]);

  const beoordeeld = summary.up + summary.down;
  const tevredenheid = beoordeeld === 0 ? null : Math.round((summary.up / beoordeeld) * 100);

  const eersteVanPagina = total === 0 ? 0 : offset + 1;
  const laatsteVanPagina = Math.min(offset + entries.length, total);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          SOEP-feedback
        </CardTitle>
        <CardDescription>
          Het oordeel van zorgverleners over gegenereerde verslagen. Eén beoordeling per
          generatie; een bijgesteld oordeel overschrijft het vorige.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* De tellers horen bij de huidige selectie, maar negeren het oordeelfilter. */}
        <div className="grid grid-cols-3 gap-3">
          <Teller
            label="Duim omhoog"
            waarde={String(summary.up)}
            icoon={<ThumbsUp className="h-4 w-4 text-primary" />}
          />
          <Teller
            label="Duim omlaag"
            waarde={String(summary.down)}
            icoon={<ThumbsDown className="h-4 w-4 text-destructive" />}
          />
          <Teller
            label="Positief"
            waarde={tevredenheid === null ? '—' : `${tevredenheid}%`}
            hint={beoordeeld === 0 ? 'nog geen beoordelingen' : `over ${beoordeeld} beoordelingen`}
          />
        </div>

        <div className="flex items-end gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Zoek in de toelichting..."
              value={zoek}
              onChange={(e) => setZoek(e.target.value)}
              className="pl-9"
            />
          </div>

          <Select
            value={oordeel}
            onValueChange={(waarde) => {
              setOordeel(waarde);
              setOffset(0);
            }}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Oordeel" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle oordelen</SelectItem>
              <SelectItem value="up">Duim omhoog</SelectItem>
              <SelectItem value="down">Duim omlaag</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={reden}
            onValueChange={(waarde) => {
              setReden(waarde);
              setOffset(0);
            }}
          >
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder="Reden" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle redenen</SelectItem>
              {tags.map((t) => (
                <SelectItem key={t.tag} value={t.tag}>
                  {REDEN_LABELS[t.tag] ?? t.tag} ({t.count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground" htmlFor="feedback-van">
              Van
            </label>
            <Input
              id="feedback-van"
              type="date"
              className="w-[150px]"
              value={van}
              onChange={(e) => {
                setVan(e.target.value);
                setOffset(0);
              }}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground" htmlFor="feedback-tot">
              Tot en met
            </label>
            <Input
              id="feedback-tot"
              type="date"
              className="w-[150px]"
              value={tot}
              onChange={(e) => {
                setTot(e.target.value);
                setOffset(0);
              }}
            />
          </div>

          <Button variant="outline" size="sm" onClick={() => void laden()} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
            Vernieuwen
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="text-xs text-muted-foreground">
              {total === 0
                ? 'Geen beoordelingen gevonden'
                : `${eersteVanPagina}–${laatsteVanPagina} van ${total} beoordelingen`}
            </div>

            <div className="border rounded-md overflow-hidden">
              <div className="overflow-x-auto max-h-[560px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0">
                    <tr className="bg-muted">
                      <th className="text-left p-2 font-medium">Tijdstip</th>
                      <th className="text-left p-2 font-medium">Oordeel</th>
                      <th className="text-left p-2 font-medium">Praktijk</th>
                      <th className="text-left p-2 font-medium">Gebruiker</th>
                      <th className="text-left p-2 font-medium">Redenen</th>
                      <th className="text-left p-2 font-medium">Toelichting</th>
                      <th className="text-left p-2 font-medium">Model</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="p-4 text-center text-muted-foreground">
                          Geen beoordelingen gevonden
                        </td>
                      </tr>
                    ) : (
                      entries.map((regel) => (
                        <tr key={regel.id} className="border-t align-top hover:bg-muted/50">
                          <td className="p-2 whitespace-nowrap font-mono">
                            {formatTijd(regel.createdAt)}
                          </td>
                          <td className="p-2">
                            {regel.rating === 'up' ? (
                              <span className="inline-flex items-center gap-1 text-primary">
                                <ThumbsUp className="h-3.5 w-3.5" />
                                Positief
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-destructive">
                                <ThumbsDown className="h-3.5 w-3.5" />
                                Negatief
                              </span>
                            )}
                          </td>
                          <td className="p-2">{regel.practiceName ?? '—'}</td>
                          <td className="p-2">{regel.userName ?? '—'}</td>
                          <td className="p-2">
                            {regel.reasonTags.length === 0 ? (
                              '—'
                            ) : (
                              <span className="flex flex-wrap gap-1">
                                {regel.reasonTags.map((t) => (
                                  <span key={t} className="rounded bg-muted px-1.5 py-0.5">
                                    {REDEN_LABELS[t] ?? t}
                                  </span>
                                ))}
                              </span>
                            )}
                          </td>
                          {/* Niet afgekapt: de toelichting is waarvoor je hier kijkt. */}
                          <td className="p-2 max-w-[360px] whitespace-pre-wrap break-words">
                            {regel.reason ?? <span className="text-muted-foreground">—</span>}
                          </td>
                          <td className="p-2 font-mono text-[11px]">{regel.model ?? '—'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGINA))}
              >
                Vorige
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={offset + entries.length >= total}
                onClick={() => setOffset(offset + PAGINA)}
              >
                Volgende
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Teller({
  label,
  waarde,
  hint,
  icoon,
}: {
  label: string;
  waarde: string;
  hint?: string;
  icoon?: ReactNode;
}) {
  return (
    <div className="rounded-md border p-3">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        {icoon}
        {label}
      </div>
      <div className="text-xl font-semibold">{waarde}</div>
      {hint && <div className="text-[11px] text-muted-foreground">{hint}</div>}
    </div>
  );
}
